import { queryOptions } from "@tanstack/react-query";
import { unwrap, type Schemas } from "@tern/api-client";
import { api } from "./api";

export type Notification = Schemas["Notification"];

export const notificationsQuery = (slug: string, filter: "all" | "unread") =>
  queryOptions({
    queryKey: ["notifications", slug, filter],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/notifications", { params: { path: { slug }, query: { filter } } })),
    staleTime: 10_000,
  });

export const unreadCountQuery = (slug: string) =>
  queryOptions({
    queryKey: ["notifications", slug, "unread-count"],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/notifications/unread-count", { params: { path: { slug } } })),
    staleTime: 10_000,
    refetchInterval: 60_000,
  });

export const patchNotification = async (id: string, body: Schemas["UpdateNotification"]) =>
  unwrap(await api.PATCH("/api/notifications/{id}", { params: { path: { id } }, body }));

export const readEntity = async (slug: string, entityType: string, entityId: string) =>
  unwrap(await api.POST("/api/workspaces/{slug}/notifications/read-entity/{entity_type}/{entity_id}", { params: { path: { slug, entity_type: entityType, entity_id: entityId } } }));

export const readAll = async (slug: string) => unwrap(await api.POST("/api/workspaces/{slug}/notifications/read-all", { params: { path: { slug } } }));
export const archiveRead = async (slug: string) => unwrap(await api.POST("/api/workspaces/{slug}/notifications/archive-read", { params: { path: { slug } } }));

export const subscriptionQuery = (issueId: string) =>
  queryOptions({
    queryKey: ["subscription", issueId],
    queryFn: async () => unwrap(await api.GET("/api/issues/{id}/subscription", { params: { path: { id: issueId } } })),
  });

export const setSubscription = async (issueId: string, subscribed: boolean) =>
  unwrap(await api.PUT("/api/issues/{id}/subscription", { params: { path: { id: issueId } }, body: { subscribed } }));

/** Group notifications by entity: newest first, one row per entity with a count. */
export function groupByEntity(list: Notification[]): { head: Notification; all: Notification[]; unread: number }[] {
  const map = new Map<string, Notification[]>();
  for (const n of list) {
    const k = `${n.entity_type}:${n.entity_id}`;
    (map.get(k) ?? map.set(k, []).get(k)!).push(n);
  }
  return [...map.values()]
    .map((all) => ({ head: all[0]!, all, unread: all.filter((n) => !n.read_at).length }))
    .sort((a, b) => (a.head.created_at < b.head.created_at ? 1 : -1));
}

/** Snooze presets → ISO timestamps. */
export function snoozeUntil(preset: "hour" | "tomorrow" | "nextWeek"): string {
  const d = new Date();
  if (preset === "hour") d.setHours(d.getHours() + 1);
  if (preset === "tomorrow") {
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
  }
  if (preset === "nextWeek") {
    d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
    d.setHours(9, 0, 0, 0);
  }
  return d.toISOString();
}
